import React, { useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
}

export const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUpload }) => {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setError('');
    try {
      await onUpload(selectedFile);
      setSelectedFile(null);
      onClose();
    } catch (err: any) {
      setError(err.message || t('err_upload_file') || 'Błąd podczas wgrywania pliku');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={() => {
          setSelectedFile(null);
          setError('');
          onClose();
      }} 
      title={t('doc_upload_title')}
    >
      {!selectedFile ? (
        <div 
          className="cab-upload-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--cab-violet)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cab-upload-ic">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p className="cab-upload-txt">{t('upload_txt')}</p>
          <p className="cab-upload-max">{t('upload_max')}</p>
          <p className="cab-upload-max">{t('upload_formats')}</p>
          
          <input 
            type="file" 
            ref={fileInputRef} 
            style={{ display: 'none' }} 
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />
        </div>
      ) : (
        <div className="cab-uploaded-file">
          <div className="cab-uf-icon">
           <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.5 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7.5L14.5 2Z" stroke="#361777" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M14 2V8H20" stroke="#361777" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M16 13H8" stroke="#361777" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M16 17H8" stroke="#361777" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M10 9H8" stroke="#361777" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
          </div>
          <div className="cab-uf-info">
            <p className="cab-uf-title">{t('doc_uploaded_file')}</p>
            <p className="cab-uf-name">{selectedFile.name}</p>
          </div>
          <button className="cab-uf-remove" onClick={() => setSelectedFile(null)} aria-label={t('doc_delete_selected')} title={t('doc_delete_selected')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
              <path d="M10 11v6"></path>
              <path d="M14 11v6"></path>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
            </svg>
          </button>
        </div>
      )}

      {error && <div className="cab-error-msg" style={{ marginTop: '10px' }}>{error}</div>}

      <div className="cab-um-actions">
        <button className="cab-btn-zamknij" onClick={() => { setSelectedFile(null); setError(''); onClose(); }}>
          {t('doc_close')}
        </button>
        <button 
          className={`cab-btn-dodaj ${selectedFile ? 'active' : ''}`}
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
        >
          {t('add_file')}
        </button>
      </div>

      <style>{`
        .cab-upload-zone {
          border: 1px dashed #D6C1F1;
          border-radius: 16px;
          padding: 40px 20px;
          text-align: center;
          background: #FBFAFF;
          cursor: pointer;
          transition: 0.2s;
          margin-bottom: 24px;
        }
        .cab-upload-zone:hover {
          background: #F4F0FA;
          border-color: var(--cab-violet);
        }
        .cab-upload-ic {
          display: block;
          margin: 0 auto 16px auto;
          color: var(--cab-violet);
        }
        .cab-upload-txt {
          font-size: 14px;
          font-weight: 600;
          color: var(--cab-violet);
          margin: 0 0 4px;
        }
        .cab-upload-max {
          font-size: 13px;
          color: var(--cab-note);
          margin: 0;
        }
        
        .cab-uploaded-file {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          border-radius: 16px;
          background: #FBFAFF;
          border: none;
          margin-bottom: 24px;
        }
        .cab-uf-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: #F4F0FA;
          color: var(--cab-violet);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cab-uf-info {
          flex: 1;
          min-width: 0;
        }
        .cab-uf-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--cab-violet);
          margin: 0 0 4px;
        }
        .cab-uf-name {
          font-size: 13px;
          color: var(--cab-violet);
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cab-uf-remove {
          width: 36px;
          height: 36px;
          background: transparent;
          border: none;
          color: var(--cab-violet);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: 0.2s;
        }
        .cab-uf-remove:hover {
          color: #E53E3E;
        }

        .cab-um-actions {
          display: flex;
          gap: 12px;
        }
        .cab-btn-zamknij,
        .cab-btn-dodaj {
          flex: 1;
          height: 44px;
          border-radius: 100px;
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: 0.2s;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cab-btn-zamknij {
          background: #fff;
          border: 1.5px solid var(--cab-violet);
          color: var(--cab-violet);
        }
        .cab-btn-zamknij:hover {
          background: #F4F0FA;
        }
        .cab-btn-dodaj {
          background: #F4F0FA;
          color: var(--cab-violet);
        }
        .cab-btn-dodaj.active {
          background: var(--cab-violet);
          color: #fff;
        }
        .cab-btn-dodaj:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
      `}</style>
    </Modal>
  );
};
